(() => {
  if (!document.location.pathname.includes('/pages/about-us') &&
    !document.location.pathname.includes('/pages/transparency')) {
    return;
  }

  const preventScroll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  document.documentElement.style.overflow = 'hidden';
  document.documentElement.style.position = 'fixed';
  document.documentElement.style.width = '100%';
  document.documentElement.style.top = '0';

  window.addEventListener('scroll', preventScroll, { passive: false });
  window.addEventListener('wheel', preventScroll, { passive: false });
  window.addEventListener('touchmove', preventScroll, { passive: false });

  window._aboutUsScrollLocked = true;
  window._aboutUsScrollListeners = { preventScroll };
})();

class AboutUsBase extends EventTarget {
  constructor() {
    super();
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
  }

  destroy() {
    if (!this.initialized) return;
    this.initialized = false;
  }

  emit(eventName, detail = {}) {
    this.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  on(eventName, handler) {
    this.addEventListener(eventName, handler);
  }

  off(eventName, handler) {
    this.removeEventListener(eventName, handler);
  }
}

class AboutUsController extends AboutUsBase {
  constructor() {
    super();
    this.modules = new Map();
    this.resizeTimeout = null;
  }

  init() {
    super.init();
    this.setupModules();
    this.bindEvents();
  }

  setupModules() {
    this.modules = new Map([
      ['animations', new AboutUsAnimations()],
      ['cards', new AboutUsCards()],
      ['spa', new AboutUsSpa(this)]
    ]);

    this.modules.forEach(module => module.init());
  }

  bindEvents() {
    this.modules.get('spa').on('sectionChanged', (e) => {
      this.emit('sectionChanged', e.detail);
    });
    
    /*
    window.addEventListener('resize', () => {
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout);

      this.resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 250);
    });
    */
  }

  handleResize() {
    this.modules.forEach(module => {
      if (module.handleResize) module.handleResize();
    });
  }

  resetSectionAnimations() {
    requestAnimationFrame(() => {
      this.modules.get('animations').resetSection();
      this.modules.get('cards').handleResize();
    });
  }

  goToSection(sectionId) {
    return this.modules.get('spa').goToSection(sectionId);
  }

  getCurrentSection() {
    return this.modules.get('spa').getCurrentSection();
  }

  destroy() {
    super.destroy();

    this.modules.forEach(module => {
      if (module.destroy) module.destroy();
    });

    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);

    /*
    window.removeEventListener('resize', this.handleResize);
    */

    this.modules.clear();
  }
}

class AboutUsAnimations extends AboutUsBase {
  static debugMarkers = false;
  
  constructor() {
    super();
    this.contexts = [];
    this.breakpoint = 992;
    AboutUsAnimations.debugMarkers = AboutUsAnimations.checkDebugMode();
  }

  static checkDebugMode() {
    let debugMode = false;
    const checkTheme = typeof window.Shopify?.theme?.role !== 'undefined' && window.Shopify?.theme?.role !== 'main';
    if (window._aboutUsSettings?.checkDebugMarkers || checkTheme) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('gsapdebug') === 'true') {
        window.sessionStorage.gsapdebug = 'true';
      } else if (urlParams.get('gsapdebug') === 'false') {
        window.sessionStorage.gsapdebug = 'false';
      }
      debugMode = window.sessionStorage?.gsapdebug === 'true';
    }

    if (debugMode) {
      const debugStylesId = 'oa-gsap-debug-styles';
      if (!document.getElementById(debugStylesId)) {
        const debugStyles = document.createElement('style');
        debugStyles.id = debugStylesId;
        debugStyles.innerHTML = `.oa-gsap-debug .gsap-marker-start, .oa-gsap-debug .gsap-marker-end, .oa-gsap-debug .gsap-marker-scroller-start, .oa-gsap-debug .gsap-marker-scroller-end { z-index: 999999999999 !important; }`;
        document.body.appendChild(debugStyles);
      }
      document.documentElement.classList.add('oa-gsap-debug');
    }

    return debugMode;
  }
  
  static getDebugMarkerId(element) {
    if (!AboutUsAnimations.debugMarkers) {
      return;
    }
    const classes = [
      'oa-about-section--',
      'oa-about-section__banner--',
      'oa-about-section__column--',
      'oa-hero-banner__item--empowering'
    ];
    return Array.of(...element.closest('.oa-about-section').classList,...element.classList)
      .filter(c => classes.some(cls => c.startsWith(cls)))
      .map(c => c.split('--')[1])
      .join('-');
  }

  init() {
    super.init();

    if (!window.gsap || !window.ScrollTrigger) {
      console.warn('GSAP or ScrollTrigger not loaded');
      return;
    }

    this.setupGSAP();
    this.initAnimations();
  }

  setupGSAP() {
    gsap.registerPlugin(ScrollTrigger);

    ScrollTrigger.config({
      limitCallbacks: true,
      // syncInterval: 60,
      ignoreMobileResize: true,
      invalidateOnRefresh: true,
      immediateRender: false,
      refreshPriority: 1,
      markers: AboutUsAnimations.debugMarkers
    });

    document.body.classList.add('about-us-loading');
  }

  initAnimations() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    ScrollTrigger.clearScrollMemory('manual');
    
    this.createAnimations();
  }
  
  createAnimations() {
    this.createHeroAnimation();
    this.createColumnAnimation();
    this.createEmpoweringAnimation();

    requestAnimationFrame(() => {
      document.body.classList.remove('about-us-loading');
    });
  }
  
  createHeroAnimation() {
    const mm = gsap.matchMedia();
    this.contexts.push(mm);

    mm.add({
      isMobile: `(max-width: ${this.breakpoint - 1}px)`,
      isDesktop: `(min-width: ${this.breakpoint}px)`
    }, AboutUsHeroBanner);
  }
  
  createColumnAnimation() {
    const mm = gsap.matchMedia();
    this.contexts.push(mm);

    mm.add({
      isMobile: `(max-width: ${this.breakpoint - 1}px)`,
      isDesktop: `(min-width: ${this.breakpoint}px)`
    }, AboutUsColumnBanner);
  }

  createEmpoweringAnimation() {
    const mm = gsap.matchMedia();
    this.contexts.push(mm);

    mm.add({
      isMobile: `(max-width: ${this.breakpoint - 1}px)`,
      isDesktop: `(min-width: ${this.breakpoint}px)`
    }, AboutUsEmpoweringBanner);
  }
  
  resetSection() {
    this.destroy();
    this.createAnimations();
    ScrollTrigger.refresh();
  }
  
  handleResize() {
    ScrollTrigger.refresh();
  }
  
  destroy() {
    this.contexts.forEach(ctx => ctx.kill());
    this.contexts = [];
  }
}

function AboutUsHeroBanner(context) {
  const element = document.querySelector('.about-section--active .oa-hero-banner.oa-hero-banner--about:first-of-type');
  if (!element) {
    return;
  }
  const pageContainer = document.querySelector('.oa-content .about-sections-container');
  const containerOffset = pageContainer ? pageContainer?.offsetTop : 0;

  const content = element.querySelector('.oa-hero-banner__content');
  const banner = element.querySelector('.oa-hero-banner__item');

  if (content) {
    gsap.fromTo(content, 
      { y: 0 }, 
      {
        y: -100,
        ease: "none",
        scrollTrigger: {
          trigger: element,
          start: `top ${containerOffset}`,
          end: `bottom ${containerOffset}`,
          scrub: 0.6
        }
      }
    );
  }

  if (banner) {
    gsap.fromTo(banner, 
      { yPercent: 0 }, 
      {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: element,
          start: `top+=50px ${containerOffset}`,
          end: `bottom ${containerOffset}`,
          scrub: true
        }
      }
    );
  }
}

function AboutUsColumnBanner(context) {
  const { isMobile, isDesktop } = context.conditions;

  const sections = [
    '.oa-about-section--mission',
    '.oa-about-section--vision',
    '.oa-about-section--quote',
    '.oa-about-section--design',
    '.oa-about-section--quality',
    '.oa-about-section--women',
    '.oa-about-section--foster',
    '.oa-about-section--community',
    '.oa-about-section--power'
  ];
  const selector = sections.map(s => `.about-section--active ${s} .oa-about-section__banner`).join(',');

  const pageContainer = document.querySelector('.oa-content .about-sections-container');
  const containerOffset = pageContainer ? pageContainer?.offsetTop : 0;
  
  gsap.utils.toArray(selector).forEach(element => {
    const section = element.closest('.oa-about-section');
    let yPercent, start, end;
    if (isMobile) {
      const bannersInSection = Array.from(section.querySelectorAll('.oa-about-section__banner'));
      const position = bannersInSection.indexOf(element);
      if (1 > position) {
        yPercent = -30;
        start = 'top bottom';
      } else {
        yPercent = -35;
        start = `top+=${50 + 25 * (position - 1)}% bottom`
      }
      end = `bottom ${containerOffset}`;
    } else {
      const reverse = Boolean(
        element.closest('.oa-about-section__columns--reverse')
        || element.closest('.oa-about-section__columns--reverse-desktop')
      );
      yPercent = -30;
      const first = Boolean(element.closest(reverse ? '.oa-about-section__column--left' : '.oa-about-section__column--right'));
      start = first ? 'top bottom' : 'top+=30% bottom';
      end = `bottom ${containerOffset}`;
    }
    
    gsap.fromTo(element,
      { yPercent: 15 },
      {
        yPercent: yPercent,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start:  start,
          end: end,
          scrub: 0.6,
          id: AboutUsAnimations.getDebugMarkerId(element)
        }
      }
    );
  });
}

function AboutUsEmpoweringBanner(context) {
  const { isMobile } = context.conditions;
  const pageContainer = document.querySelector('.oa-content .about-sections-container');
  const containerOffset = pageContainer ? pageContainer?.offsetTop : 0;
  
  gsap.utils.toArray('.about-section--active .oa-about-section--empowering .oa-hero-banner__item').forEach(parent => {
    gsap.utils.toArray('.oa-about-section__parallax-wrapper .oa-about-section__banner.oa-about-section__banner-img--empowering', parent).forEach(element => {
      let height = isMobile ? element.clientHeight / 4 : element.clientHeight / 2;

      let fromY = -height;
      let toY = height;

      if (isMobile) {
        const isFirst = Boolean(element.closest('.oa-hero-banner__item--empowering1'));
        if (isFirst) {
          height = element.clientHeight / 2;
          fromY = -(height / 2);
          toY = height / 10;
        }
      }
      
      gsap.fromTo(element,
        { y: fromY },
        {
          y: toY,
          ease: 'none',
          scrollTrigger: {
            trigger: parent,
            start: 'top bottom',
            end: `bottom ${containerOffset}`,
            scrub: true,
            id: AboutUsAnimations.getDebugMarkerId(element)
          }
        }
      );
    });
    
    /*
    if (isMobile) {
      gsap.utils.toArray('.oa-hero-banner__content', parent).forEach(element => {
        gsap.fromTo(element,
          {yPercent: isMobile ? -65 : -25},
          {
            yPercent: 0,
            ease: "none",
            scrollTrigger: {
              trigger: parent,
              start: 'top bottom',
              end: `bottom bottom-=25%`,
              scrub: true
            }
          }
        );
      });
    }
    */
  });
}

class AboutUsCards extends AboutUsBase {
  constructor() {
    super();
    this.cards = [];
    this.animations = new Map();
    this.activeCard = null;
  }

  init() {
    super.init();
    this.setupCards();
    this.bindEvents();
  }

  setupCards() {
    this.cards = document.querySelectorAll('.oa-about-section--values-item');

    if (window.gsap) {
      this.setupAnimations();
    }
  }

  setupAnimations() {
    this.cards.forEach(card => {
      const content = card.querySelector('.oa-about-section--values-content');
      const text = card.querySelector('.oa-about-section--values-expanded-content');
      const overlay = card.querySelector('.oa-about-section--values-overlay');
      const background = card.querySelector('.oa-about-section--values-background');
      const plusIcon = card.querySelector('.oa-about-section--values-icon-plus');
      const minusIcon = card.querySelector('.oa-about-section--values-icon-minus');

      if (!content || !text) return;
    
      const tl = gsap.timeline({
        paused: true,
        onComplete: () =>  card.classList.add('oa-about-section--values-item--expanded'),
        onReverseComplete: () => card.classList.remove('oa-about-section--values-item--expanded')
      });

      tl.to(content, { yPercent: 50, bottom: '50%', duration: 0.6, ease: 'power2.inOut' }, 0)
        .set(text, { visibility: 'visible' }, 0)
        .fromTo(text,
          { opacity: 0, maxHeight: 0, marginTop: 0 },
          { opacity: 1, maxHeight: 200, marginTop: 20, duration: 0.6, ease: 'power2.inOut' },
          0.1
        )
        .to(overlay, { backgroundColor: 'rgba(139, 139, 139, 0.8)', duration: 0.6, ease: 'power2.inOut' }, 0)
        .to(background, { filter: 'blur(2px)', duration: 0.3, ease: "power2.out" }, 0.3);

      if (plusIcon && minusIcon) {
        tl.to(plusIcon, { opacity: 0, duration: 0.3, ease: "power2.out" }, 0)
          .to(minusIcon, { opacity: 1, duration: 0.3, ease: "power2.out" }, 0);
      }

      this.animations.set(card, tl);
    });
  }

  bindEvents() {
    this.cards.forEach(card => {
      card.addEventListener('click', () => this.toggleCard(card));
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-expanded', 'false');
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.oa-about-section--values-item')) {
        this.closeAllCards();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllCards();
      }
    });
  }

  toggleCard(card) {
    if (this.isCardExpanded(card)) {
      this.closeCard(card);
    } else {
      this.openCard(card);
    }
  }

  openCard(card) {
    this.closeAllCards(card);
    this.activeCard = card;

    if (window.gsap && this.animations.has(card)) {
      this.animations.get(card).play();
    } else {
      card.classList.add('oa-about-section--values-item--expanded');
    }

    card.setAttribute('aria-expanded', 'true');
  }

  closeCard(card) {
    if (this.activeCard === card) {
      this.activeCard = null;
    }

    if (window.gsap && this.animations.has(card)) {
      this.animations.get(card).reverse();
    } else {
      card.classList.remove('oa-about-section--values-item--expanded');
    }

    card.setAttribute('aria-expanded', 'false');
  }

  isCardExpanded(card) {
    if (window.gsap && this.animations.has(card)) {
      return this.animations.get(card).progress() > 0.5;
    }
    return card.classList.contains('oa-about-section--values-item--expanded');
  }

  closeAllCards(exceptCard) {
    this.cards.forEach(card => {
      if (card !== exceptCard) {
        this.closeCard(card);
      }
    });
  }

  handleResize() {
    if (!window.gsap) return;

    this.animations.forEach(tl => tl.kill());
    this.animations.clear();

    this.cards.forEach(card => {
      card.classList.remove('oa-about-section--values-item--expanded');
      gsap.set(card.querySelectorAll('*'), { clearProps: 'all' });
    });

    this.setupAnimations();
  }

  destroy() {
    super.destroy();
    if (window.gsap) {
      this.animations.forEach(tl => tl.kill());
      this.animations.clear();
    }

    this.cards.forEach(card => {
      card.removeAttribute('role');
      card.removeAttribute('tabindex');
      card.removeAttribute('aria-expanded');
      card.classList.remove('oa-about-section--values-item--expanded');
    });
    this.cards = [];
    this.activeCard = null;
  }
}

class AboutUsSpa extends AboutUsBase {
  constructor(controller) {
    super();
    this.controller = controller;
    this.sections = new Map();
    this.pathToSection = new Map();
    this.sectionTitles = new Map();
    this.titlesFetched = new Map();
    this.titlesFetching = new Map();
    this.originalTitle = document.title;
    this.baseTitlePattern = this.extractBaseTitlePattern();
    this.sectionNavTitles = new Map();
    this.currentSection = null;
    this.navLinks = [];
    this.handleNavClick = this.handleNavClick.bind(this);
    this.handlePopstate = this.handlePopstate.bind(this);
  }

  init() {
    super.init();
    this.navLinks = document.querySelectorAll(`
      .about-us-nav-link,
      .oa-about-us a[data-section]
    `);
    this.initializeSectionMappings();
    this.bindEvents();
    this.handleInitialLoad();
    this.preloadSectionTitles();
  }
  
  bindEvents() {
    this.navLinks.forEach(link => link.addEventListener('click', this.handleNavClick));

    window.addEventListener('popstate', this.handlePopstate);
  }

  switchToSection(targetSection, options = {}) {
    const {
      pushState = false,
      url = null,
      updateHistory = true
    } = options;

    if (targetSection === this.currentSection) return false;

    const previousSection = this.currentSection;

    this.updateSectionDOM(targetSection);

    if (updateHistory && pushState && url) {
      history.pushState({ section: targetSection }, '', url);
    }

    this.updateUIState(targetSection);

    this.currentSection = targetSection;

    this.emit('sectionChanged', {
      section: targetSection,
      oldSection: previousSection,
      url: url
    });

    return true;
  }

  updateSectionDOM(targetSection) {
    const allSections = document.querySelectorAll('.about-section');
    const targetElement = document.getElementById(targetSection);

    if (!targetElement) {
      console.warn(`Section element not found: ${targetSection}`);
      return false;
    }

    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });

    allSections.forEach(section => {
      section.classList.remove('about-section--active');
    });
    targetElement.classList.add('about-section--active');

    this.controller.resetSectionAnimations();

    this.updateActiveSwiper();

    return true;
  }

  updateUIState(targetSection) {
    this.updateNavigation(targetSection);
    void this.updatePageTitle(targetSection);
  }

  navigateToSection(targetSection, targetUrl, pushState = true) {
    return this.switchToSection(targetSection, {
      pushState,
      url: targetUrl,
      updateHistory: true
    });
  }

  handleNavClick(e) {
    e.preventDefault();

    const targetSection = e.target.getAttribute('data-section');
    const targetUrl = e.target.getAttribute('href');

    if (targetSection && targetSection !== this.currentSection) {
      this.navigateToSection(targetSection, targetUrl, true);
    }
  }

  handlePopstate(e) {
    const targetSection = e.state && e.state.section
      ? e.state.section
      : this.pathToSection.get(window.location.pathname);

    if (targetSection && targetSection !== this.currentSection) {
      this.switchToSection(targetSection, {
        pushState: false,
        url: null,
        updateHistory: false
      });
    }
  }

  handleInitialLoad() {
    const currentPath = window.location.pathname;
    let targetSection = this.pathToSection.get(currentPath);

    if (currentPath === '/pages/about-us') {
      targetSection = 'who-we-are';
      const targetUrl = this.sections.get(targetSection);
      if (targetUrl) {
        history.replaceState({ section: targetSection }, '', targetUrl);
      }
    }

    targetSection = targetSection || this.getFirstSection();

    this.switchToSection(targetSection, {
      pushState: false,
      url: null,
      updateHistory: false
    });

    if (currentPath !== '/pages/about-us') {
      const targetUrl = this.sections.get(targetSection);
      if (targetUrl && currentPath !== targetUrl) {
        history.replaceState({ section: targetSection }, '', targetUrl);
      }
    }
  }

  goToSection(sectionId) {
    const targetUrl = this.sections.get(sectionId);
    if (targetUrl && sectionId !== this.currentSection) {
      this.navigateToSection(sectionId, targetUrl, true);
    }
  }

  getCurrentSection() {
    return this.currentSection;
  }

  getSectionData(sectionId) {
    return {
      id: sectionId,
      path: this.sections.get(sectionId),
      title: this.sectionTitles.get(sectionId),
      navTitle: this.sectionNavTitles.get(sectionId),
      isActive: sectionId === this.currentSection
    };
  }

  initializeSectionMappings() {
    const sectionElements = document.querySelectorAll('.about-section[data-section][data-path]');

    sectionElements.forEach(element => {
      const section = element.dataset.section;
      const path = element.dataset.path;

      if (section && path) {
        this.sections.set(section, path);
        this.pathToSection.set(path, section);

        const title = element.dataset.title;
        const navTitle = element.dataset.navTitle;
        
        if (title) {
          this.sectionTitles.set(section, title);
          this.titlesFetched.set(section, true);
        }

        if (navTitle) {
          this.sectionNavTitles.set(section, navTitle);
        }
      }
    });

    if (this.sections.size > 0) {
      const firstSection = this.sections.keys().next().value;
      this.pathToSection.set('/pages/about-us', firstSection);
    }

    if (this.sections.size === 0) {
      console.warn('No sections found with data attributes, falling back to hardcoded values');
      this.sections = new Map([
        ['who-we-are', '/pages/about-us-who-we-are'],
        ['what-we-do', '/pages/about-us-what-we-do'],
        ['who-we-are-for', '/pages/about-us-who-we-are-for'],
        ['transparency', '/pages/transparency']
      ]);

      this.pathToSection = new Map([
        ['/pages/about-us', 'who-we-are'],
        ['/pages/about-us-who-we-are', 'who-we-are'],
        ['/pages/about-us-what-we-do', 'what-we-do'],
        ['/pages/about-us-who-we-are-for', 'who-we-are-for'],
        ['/pages/transparency', 'transparency']
      ]);
    }
  }

  getFirstSection() {
    return this.sections.keys().next().value || 'who-we-are';
  }
  
  updateNavigation(activeSection) {
    const navLinks = document.querySelectorAll('.about-us-nav-link');
    navLinks.forEach(link => {
      const linkSection = link.dataset.section;
      if (linkSection === activeSection) {
        link.classList.add('oa-hdr19-bottom__link--active');
      } else {
        link.classList.remove('oa-hdr19-bottom__link--active');
      }
    });
  }

  updateActiveSwiper() {
    document.querySelectorAll('.about-section.about-section--active .swiper-container').forEach(element => {
      if (element.swiper) {
        element.swiper.update();
      }
    });
  }

  async updatePageTitle(section) {
    try {
      if (this.sectionTitles.has(section)) {
        const sectionTitle = this.sectionTitles.get(section);
        const newTitle = sectionTitle + this.baseTitlePattern;
        document.title = newTitle;
        return;
      }

      const sectionTitle = await this.fetchSectionTitle(section);

      if (sectionTitle) {
        const newTitle = sectionTitle + this.baseTitlePattern;
        document.title = newTitle;

        if (history.state && history.state.section === section) {
          history.replaceState({ section: section }, newTitle);
        }
      }
    } catch (error) {
      console.warn(`Failed to update page title for section ${section}:`, error);
      const fallbackTitle = this.sectionNavTitles.get(section) ||
        section.split('-').map(word =>
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
      document.title = fallbackTitle + this.baseTitlePattern;
    }
  }

  async fetchSectionTitle(section) {
    if (this.titlesFetched.has(section)) {
      return this.sectionTitles.get(section);
    }

    if (this.titlesFetching.has(section)) {
      return this.titlesFetching.get(section);
    }

    const sectionUrl = this.sections.get(section);
    if (!sectionUrl) {
      console.warn(`No URL found for section: ${section}`);
      return '';
    }

    const apiUrl = `${sectionUrl}?view=about-us-content`;

    const fetchPromise = this.performTitleFetch(apiUrl, section);
    this.titlesFetching.set(section, fetchPromise);

    try {
      const title = await fetchPromise;

      this.sectionTitles.set(section, title);
      this.titlesFetched.set(section, true);
      this.titlesFetching.delete(section);

      return title;
    } catch (error) {
      console.warn(`Failed to fetch title for section ${section}:`, error);
      this.titlesFetching.delete(section);

      const fallbackTitle = this.sectionNavTitles.get(section) ||
        section.split('-').map(word =>
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');

      this.sectionTitles.set(section, fallbackTitle);
      this.titlesFetched.set(section, true);

      return fallbackTitle;
    }
  }

  async performTitleFetch(apiUrl, section) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();
      return this.extractTitleFromHtml(html, section);
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  async getSectionTitle(section) {
    try {
      return await this.fetchSectionTitle(section);
    } catch (error) {
      console.warn(`Failed to get title for section ${section}:`, error);
      return '';
    }
  }

  isTitleCached(section) {
    return this.titlesFetched.has(section);
  }

  extractTitleFromHtml(html, section) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const titleElement = doc.querySelector('head title');
      if (titleElement && titleElement.textContent) {
        const fullTitle = titleElement.textContent.trim();
        const separatorPatterns = [' | ', ' - ', ' • ', ' / '];

        for (let pattern of separatorPatterns) {
          if (fullTitle.includes(pattern)) {
            const parts = fullTitle.split(pattern);
            if (parts.length >= 2) {
              return parts[0].trim();
            }
          }
        }

        return fullTitle;
      }

      throw new Error('No title element found');
    } catch (error) {
      console.warn(`Error parsing HTML for section ${section}:`, error);
      throw error;
    }
  }

  async preloadSectionTitles() {
    const sections = Array.from(this.sections.keys());
    const promises = sections.map(section => this.fetchSectionTitle(section));

    try {
      await Promise.allSettled(promises);
    } catch (error) {
      console.warn('Some section titles could not be preloaded:', error);
    }
  }

  extractBaseTitlePattern() {
    const title = this.originalTitle || document.title;
    const patterns = [
      / \| /, // " | "
      / - /, // " - "
      / • /, // " • "
      / \/ /, // " / "
    ];

    for (let pattern of patterns) {
      if (pattern.test(title)) {
        const parts = title.split(pattern);
        if (parts.length >= 2) {
          return title.substring(parts[0].length);
        }
      }
    }

    return '';
  }

  getAvailableSections() {
    return Array.from(this.sections.keys());
  }

  destroy() {
    super.destroy();
    this.navLinks.forEach(link => link.removeEventListener('click', this.handleNavClick));
    window.removeEventListener('popstate', this.handlePopstate);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!document.querySelector('.oa-about-us, .oa-transparency')) {
    return;
  }

  window.scrollTo(0, 0);

  requestAnimationFrame(() => {
    if (window._aboutUsScrollLocked) {
      if (window._aboutUsScrollListeners) {
        window.removeEventListener('scroll', window._aboutUsScrollListeners.preventScroll);
        window.removeEventListener('wheel', window._aboutUsScrollListeners.preventScroll);
        window.removeEventListener('touchmove', window._aboutUsScrollListeners.preventScroll);
      }
      
      document.documentElement.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.width = '';
      document.documentElement.style.top = '';
       
      window._aboutUsScrollLocked = false;
    }
    
    window.aboutUsController = new AboutUsController();
    window.aboutUsController.init();
  });
});